// Visual vocabulary of the code map, in Orbital's two themes: one hue per node
// kind, the backend and frontend repos as two families (glacier blue, sea teal),
// domain nodes as accents, and the activity colours the whole console uses: read
// (blue), edit (lilac), probe (amber). sigma draws in WebGL, so colours are hex,
// not CSS variables: applyGraphTheme() swaps the palette when the theme changes and
// CodeMap redraws.
import type { GraphNode } from "@/api/types";
import type { Theme } from "@/lib/theme";

interface Palette {
  bg: [number, number, number];          // the map's background, for blending edge colours
  kind: Record<string, string>;
  tint: Record<"backend" | "frontend", Record<string, string>>;
  edge: Record<string, string>;
  fallbackNode: string;
  fallbackEdge: string;
  dim: string;                          // receded nodes
  dimPackage: string;
  mutedLabel: string;
  label: string;
  flowDim: string;                      // non-data nodes in Data flow
  ambiguous: string;
  ephemeral: string;
  agg: { flow: string; backend: string; frontend: string };
  activity: { read: string; edit: string; probe: string; ticket: string; blast: string[] };
  overlay: {
    halo: Record<"backend" | "frontend", string>; haloLabel: Record<"backend" | "frontend", string>;
    ephemeralRing: string; trail: string; badgeBg: string; badgeText: string; pullRing: string; head: string;
  };
}

const DARK: Palette = {
  bg: [11, 16, 22],
  kind: {
    repo: "#9aa4b6", package: "#7f8aa0", module: "#8fc7ee", symbol: "#56718c",
    contract_event: "#efc177", bus_event: "#d9b45e", lg_agent: "#e6a3c6", lg_tool: "#b98aa8",
    aws_resource: "#f2a35e", http_route: "#91cbd6",
  },
  tint: {
    backend: { package: "#6f86a8", module: "#8fc7ee", symbol: "#4f6d8c" },
    frontend: { package: "#5f9a94", module: "#91cbd6", symbol: "#3f6f72" },
  },
  edge: {
    contains: "rgba(127,138,160,0.10)", imports: "rgba(143,199,238,0.26)", calls: "rgba(143,199,238,0.20)",
    inherits: "rgba(185,169,222,0.40)", implements: "rgba(185,169,222,0.40)", defines: "rgba(239,193,119,0.40)",
    emits: "rgba(239,193,119,0.85)", handles: "rgba(145,203,214,0.85)", publishes: "rgba(217,180,94,0.70)",
    subscribes: "rgba(217,180,94,0.70)", registers: "rgba(230,163,198,0.60)", has_tool: "rgba(185,138,168,0.55)",
    uses_resource: "rgba(242,163,94,0.70)", defines_resource: "rgba(242,163,94,0.45)",
    calls_route: "rgba(145,203,214,0.75)", serves_route: "rgba(145,203,214,0.50)",
  },
  fallbackNode: "#7f8aa0", fallbackEdge: "rgba(127,138,160,0.18)",
  dim: "#1f2833", dimPackage: "#2a3442", mutedLabel: "#5d6878", label: "#cfd3df", flowDim: "#26303c",
  ambiguous: "rgba(127,138,160,0.12)", ephemeral: "#5d6878",
  agg: { flow: "rgba(239,193,119,0.70)", backend: "rgba(143,199,238,0.40)", frontend: "rgba(145,203,214,0.40)" },
  activity: { read: "#8fc7ee", edit: "#b9a9de", probe: "#efc177", ticket: "#a8d5f4", blast: ["#f7959d", "#f2b38a", "#efc177", "#cfc49a"] },
  overlay: {
    halo: { backend: "rgba(143,199,238,0.07)", frontend: "rgba(145,203,214,0.07)" },
    haloLabel: { backend: "rgba(168,213,244,0.62)", frontend: "rgba(145,203,214,0.62)" },
    ephemeralRing: "rgba(163,168,186,0.75)", trail: "168,213,244", badgeBg: "rgba(25,30,39,0.94)", badgeText: "#eeeff5",
    pullRing: "rgba(168,213,244,0.6)", head: "239,193,119",
  },
};

const LIGHT: Palette = {
  bg: [233, 237, 240],
  kind: {
    repo: "#1d2a36", package: "#586774", module: "#266698", symbol: "#9ab3c8",
    contract_event: "#8b5b0b", bus_event: "#a87a1c", lg_agent: "#a3437a", lg_tool: "#c06d9c",
    aws_resource: "#b5651d", http_route: "#256f7d",
  },
  tint: {
    backend: { package: "#3f5f80", module: "#266698", symbol: "#9ab9d4" },
    frontend: { package: "#2f6b66", module: "#256f7d", symbol: "#94c6c4" },
  },
  edge: {
    contains: "rgba(88,103,116,0.10)", imports: "rgba(38,102,152,0.26)", calls: "rgba(38,102,152,0.20)",
    inherits: "rgba(108,84,152,0.38)", implements: "rgba(108,84,152,0.38)", defines: "rgba(139,91,11,0.40)",
    emits: "rgba(139,91,11,0.80)", handles: "rgba(37,111,125,0.80)", publishes: "rgba(168,122,28,0.70)",
    subscribes: "rgba(168,122,28,0.70)", registers: "rgba(163,67,122,0.60)", has_tool: "rgba(192,109,156,0.55)",
    uses_resource: "rgba(181,101,29,0.70)", defines_resource: "rgba(181,101,29,0.45)",
    calls_route: "rgba(37,111,125,0.75)", serves_route: "rgba(37,111,125,0.50)",
  },
  fallbackNode: "#8495a3", fallbackEdge: "rgba(88,103,116,0.18)",
  dim: "#d9e1e7", dimPackage: "#cdd7df", mutedLabel: "#9aa8b4", label: "#1d2a36", flowDim: "#d4dde4",
  ambiguous: "rgba(88,103,116,0.12)", ephemeral: "#9aa8b4",
  agg: { flow: "rgba(139,91,11,0.65)", backend: "rgba(38,102,152,0.40)", frontend: "rgba(37,111,125,0.40)" },
  activity: { read: "#266698", edit: "#6c5498", probe: "#895c12", ticket: "#235d88", blast: ["#b53848", "#c8613f", "#b07a1a", "#a8955a"] },
  overlay: {
    halo: { backend: "rgba(38,102,152,0.07)", frontend: "rgba(37,111,125,0.07)" },
    haloLabel: { backend: "rgba(35,93,136,0.7)", frontend: "rgba(37,111,125,0.7)" },
    ephemeralRing: "rgba(88,103,116,0.75)", trail: "35,93,136", badgeBg: "rgba(29,42,54,0.9)", badgeText: "#ffffff",
    pullRing: "rgba(35,93,136,0.55)", head: "139,91,11",
  },
};

/** The palette in use. Mutated in place by applyGraphTheme; read it at draw time. */
export const G: Palette = structuredClone(DARK);

// Kept for the views that read them (the map legend); always the current palette's.
export const KIND_COLOR: Record<string, string> = G.kind;
export const EDGE_COLOR: Record<string, string> = G.edge;
export const ACTIVITY = G.activity;

export function applyGraphTheme(theme: Theme): void {
  const src = theme === "light" ? LIGHT : DARK;
  const fresh = structuredClone(src);
  // Same objects, new values: the exported aliases above stay valid.
  for (const k of Object.keys(G.kind)) delete G.kind[k];
  Object.assign(G.kind, fresh.kind);
  for (const k of Object.keys(G.edge)) delete G.edge[k];
  Object.assign(G.edge, fresh.edge);
  Object.assign(G.activity, fresh.activity);
  const { kind: _k, edge: _e, activity: _a, ...rest } = fresh;
  Object.assign(G, rest);
  solidCache.clear();
}

// Backend and frontend read as two families so cross-repo edges stand out.
export function repoSide(repo: string | null | undefined, sides: Record<string, string>): string | undefined {
  return repo ? sides[repo] : undefined;
}

export function nodeColor(n: Pick<GraphNode, "kind" | "repo">, sides: Record<string, string>): string {
  const side = repoSide(n.repo, sides) as "backend" | "frontend" | undefined;
  if (side && G.tint[side]?.[n.kind]) return G.tint[side][n.kind];
  return G.kind[n.kind] ?? G.fallbackNode;
}

// sigma's WebGL output isn't premultiplied, so a faint rgba edge composites badly
// over the page. Edges get opaque colours instead: the rgba blended over the map's
// background (cached; reducers run per edge per frame).
const solidCache = new Map<string, string>();
export function solid(color: string): string {
  const hit = solidCache.get(color);
  if (hit) return hit;
  const m = color.match(/^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)$/);
  let out = color;
  if (m) {
    const a = Math.max(0, Math.min(1, Number(m[4])));
    const ch = (i: number) => Math.round(G.bg[i] * (1 - a) + Number(m[i + 1]) * a).toString(16).padStart(2, "0");
    out = `#${ch(0)}${ch(1)}${ch(2)}`;
  }
  solidCache.set(color, out);
  return out;
}

/** An rgba colour at another opacity. */
export function alpha(color: string, a: number): string {
  return color.replace(/[\d.]+\)$/, `${a})`);
}

// Edges that carry data at runtime — animated in "Data flow" mode.
export const FLOW_KINDS = new Set(["emits", "handles", "publishes", "subscribes", "uses_resource", "calls_route", "serves_route"]);

export const KIND_LABEL: Record<string, string> = {
  repo: "Repository", package: "Package", module: "Module", symbol: "Symbol",
  contract_event: "Wire event", bus_event: "Bus event", lg_agent: "LangGraph agent", lg_tool: "Agent tool",
  aws_resource: "AWS resource", http_route: "HTTP route",
};
